'use client'
import { PrismicRichText } from "@prismicio/react";
import { PrismicNextImage } from "@prismicio/next";
import { isFilled } from "@prismicio/client";
import Marquee from "react-fast-marquee";

/**
 * @typedef {import("@prismicio/client").Content.HeroProjectSlice} HeroProjectSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<HeroProjectSlice>} HeroProjectProps
 * @type {import("react").FC<HeroProjectProps>}
 */
const HeroProject = ({ slice, index, slices }) => {
  const isLast = Array.isArray(slices) && index === slices.length - 1;
  const fallbackAlt = "Image de contenu";

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`bg-white ${isLast ? "md:pb-10" : ""}`}
    >
      <div className="w-full max-w-450 w-auto mx-auto pt-20">
        <div className="flex flex-col gap-4 md:gap-2">
          <div className="flex flex-col lg:flex-row gap-8 lg:items-center px-4 md:px-10">
            <div className="h-[1px] w-full bg-light-gray lg:flex-1"></div>
            <h1 className="project-title">{slice.primary.subtitle}</h1>
          </div>
          {isFilled.keyText(slice.primary.title) && (
            <>
              <h2 className="sr-only">{slice.primary.title}</h2>
              <Marquee
                gradient={false}
                speed={80}
                pauseOnHover={false}
                aria-hidden="true"
              >
                {Array.from({ length: 4 }).map((_, titleIndex) => (
                  <span key={titleIndex} className="marquee-text uppercase pr-4" aria-hidden="true">
                    {slice.primary.title}
                  </span>
                ))}
              </Marquee>
            </>
          )}
          <div className="flex justify-end px-4 md:px-10 mb-10 md:mb-0">
            <div className="w-fit">
              {isFilled.richText(slice.primary.tags) && (
                <PrismicRichText
                  field={slice.primary.tags}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="font-neue-haas text-base lg:text-xl xl:text-2xl font-normal uppercase text-end border-t-1 border-light-gray last:border-b-1">{children}</p>
                    ),
                  }}
                />
              )}
            </div>
          </div>
          <div className="px-4 md:px-10 flex justify-between w-full items-end gap-10">
            <div className="w-full">
              {isFilled.richText(slice.primary.text) && (
                <PrismicRichText
                  field={slice.primary.text}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="font-neue-haas text-lg font-light">{children}</p>
                    ),
                    em: ({ children }) => (
                      <em className="italic">{children}</em>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-medium">{children}</strong>
                    ),
                    image: ({ node }) => (
                      <PrismicNextImage
                        field={node}
                        alt={node?.alt || fallbackAlt}
                        className="w-full max-w-100 mx-auto"
                      />
                    ),
                  }}
                />
              )}
            </div>
            <div className={`${slice.variation === "default" ? "block" : "hidden"}`}>
              <img src="/mw-arrow.svg" alt="flèche" className="w-16 lg:w-30" />
            </div>
          </div>
          <div className={`${slice.variation === "default" ? "block" : "flex justify-between relative items-end gap-5 pl-4 mt-10 md:pl-10"}`}>
            {isFilled.image(slice.primary.img) && (
              <PrismicNextImage field={slice.primary.img} alt={slice.primary.img?.alt || fallbackAlt} className={`${slice.variation === "default" ? "" : "max-w-60 md:max-w-75 flex-1"} w-full`} />
            )}
            <div className={`${slice.variation === "default" ? "hidden" : "block h-fit absolute right-4 md:right-10 bottom-0"}`}>
              <img src="/mw-arrow.svg" alt="flèche" className="w-16 h-auto lg:w-30" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroProject;